import { useState } from "react";
import { GraduationCap, PencilLine, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiFetch } from "@/api";

/* Abhyas 2.0 — shared academic context control.
   Board + Class live in the GLOBAL profile; every practice feature shows the
   current context and offers "Change Data" — which updates the profile
   everywhere. History is never touched (backend only sets board/klass). */

const BOARDS = [
  { value: "CBSE", label: "CBSE" },
  { value: "ICSE/ISC", label: "ICSE" },
];
const CLASSES = ["9", "10", "11", "12"];

const pill = (active) =>
  `rounded-full border-2 px-5 py-2.5 text-sm font-bold transition-[border-color,background-color] duration-200 ${
    active
      ? "border-[#16A34A] bg-[#16A34A]/10 text-[#17201A]"
      : "border-[#17201A]/10 bg-[#F8FAF7] text-[#17201A]/60 hover:border-[#16A34A]/40"
  }`;

const boardLabel = (b) => (b === "CBSE" ? "CBSE" : "ICSE");

const AcademicContextBar = ({ board, klass, onChanged, testId = "academic-context" }) => {
  const [open, setOpen] = useState(false);
  const [selBoard, setSelBoard] = useState(null);
  const [selKlass, setSelKlass] = useState(null);
  const [saving, setSaving] = useState(false);

  const openModal = () => {
    setSelBoard(board === "CBSE" ? "CBSE" : "ICSE/ISC");
    setSelKlass(String(klass || ""));
    setOpen(true);
  };

  const save = async () => {
    if (!selBoard || !selKlass) return;
    setSaving(true);
    try {
      const d = await apiFetch("/profile/academic", {
        method: "POST",
        body: { board: selBoard, klass: selKlass },
      });
      setOpen(false);
      toast.success("Academic context updated");
      onChanged?.(d.user.board, String(d.user.klass));
    } catch (err) {
      toast.error(err.message || "Couldn't update — try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        data-testid={testId}
        className="inline-flex flex-wrap items-center gap-2 rounded-full border border-[#E3ECE6] bg-white px-4 py-2"
      >
        <GraduationCap className="h-4 w-4 text-[#16A34A]" />
        <span data-testid={`${testId}-label`} className="text-xs font-bold text-[#17201A]">
          {boardLabel(board)} · Class {klass}
        </span>
        <button
          type="button"
          data-testid={`${testId}-change-btn`}
          onClick={openModal}
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold text-[#16A34A] transition-colors duration-200 hover:bg-[#16A34A]/8"
        >
          <PencilLine className="h-3 w-3" />
          Change Data
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${testId}-modal`} className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold">
              Change Academic Data
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs font-semibold leading-relaxed text-[#647067]">
            This updates your Board and Class everywhere in Abhyas. Your past
            practice history and progress stay untouched.
          </p>
          <p className="mt-4 text-xs font-bold uppercase tracking-wider text-[#647067]">Board</p>
          <div className="mt-2 flex gap-2">
            {BOARDS.map((b) => (
              <button
                key={b.value}
                type="button"
                data-testid={`${testId}-board-${b.label.toLowerCase()}`}
                onClick={() => setSelBoard(b.value)}
                className={pill(selBoard === b.value)}
              >
                {b.label}
              </button>
            ))}
          </div>
          <p className="mt-4 text-xs font-bold uppercase tracking-wider text-[#647067]">Class</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {CLASSES.map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`${testId}-class-${c}`}
                onClick={() => setSelKlass(c)}
                className={pill(selKlass === c)}
              >
                Class {c}
              </button>
            ))}
          </div>
          <button
            type="button"
            data-testid={`${testId}-save-btn`}
            onClick={save}
            disabled={!selBoard || !selKlass || saving}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#16A34A] py-3.5 text-sm font-bold text-white transition-[background-color,opacity] duration-200 hover:bg-[#15803D] disabled:opacity-40"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AcademicContextBar;
